import { easeOut, hash2 } from "./noise.js";

/**
 * A spark (DESIGN.md §9, "Pixel art", "Drops", "Spark"). Seven cells over
 * 14px read as too much on every press; cut twice on 2026-10-13, by half and
 * then by another 30%.
 */
const CELLS = 3;
const LIFE_MS = 600;
const GROW_MS = 120;
const STAGGER_MS = 90;
/** How far a cell drifts out from where it started. */
const DRIFT = 5;
/** Fully there until this share of its life, then fading. */
const HOLD = 0.4;

interface Cell {
  readonly x: number;
  readonly y: number;
  readonly dx: number;
  readonly dy: number;
  readonly size: number;
  readonly colour: string;
  readonly start: number;
}

/**
 * The bursts of cells on one canvas over the whole window. It draws only while
 * a burst is alive, so a still window costs nothing per frame.
 */
export class Bursts {
  private cells: readonly Cell[] = [];
  private frameId = 0;
  private count = 0;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly ctx: CanvasRenderingContext2D,
  ) {}

  /** A few cells bursting outward from (x, y), in window pixels. */
  add(x: number, y: number, colours: readonly string[]): void {
    const now = performance.now();
    this.count += 1;
    const born: Cell[] = [];
    for (let i = 0; i < CELLS; i++) {
      const at = (k: number) => hash2(i, this.count, k);
      const angle = (i / CELLS) * Math.PI * 2 + at(1) * 0.8;
      const colour = colours[Math.floor(at(6) * colours.length)];
      if (colour === undefined) continue;
      born.push({
        x: x + Math.cos(angle) * (1.5 + at(2) * 2),
        y: y + Math.sin(angle) * (1.5 + at(2) * 2),
        dx: Math.cos(angle) * DRIFT * (0.6 + at(3) * 0.4),
        // A little upward, so it rises as it goes rather than spreading flat.
        dy: Math.sin(angle) * DRIFT * (0.6 + at(4) * 0.4) - 1.5,
        size: 4 + Math.round(at(5) * 2),
        colour,
        start: now + at(7) * STAGGER_MS,
      });
    }
    this.cells = [...this.cells, ...born];
    if (this.frameId === 0) this.frameId = requestAnimationFrame(this.frame);
  }

  stop(): void {
    if (this.frameId !== 0) cancelAnimationFrame(this.frameId);
    this.frameId = 0;
  }

  private readonly frame = (now: number): void => {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const width = window.innerWidth;
    const height = window.innerHeight;
    if (this.canvas.width !== Math.round(width * dpr)) {
      this.canvas.width = Math.round(width * dpr);
    }
    if (this.canvas.height !== Math.round(height * dpr)) {
      this.canvas.height = Math.round(height * dpr);
    }
    const ctx = this.ctx;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    this.cells = this.cells.filter((cell) => now - cell.start < LIFE_MS);
    for (const cell of this.cells) {
      const age = now - cell.start;
      if (age < 0) continue;
      const life = age / LIFE_MS;
      const size = cell.size * easeOut(Math.min(1, age / GROW_MS));
      const move = easeOut(life);
      ctx.globalAlpha = life < HOLD ? 1 : 1 - (life - HOLD) / (1 - HOLD);
      ctx.fillStyle = cell.colour;
      ctx.fillRect(
        cell.x + cell.dx * move - size / 2,
        cell.y + cell.dy * move - size / 2,
        size,
        size,
      );
    }
    ctx.globalAlpha = 1;
    this.frameId = this.cells.length > 0 ? requestAnimationFrame(this.frame) : 0;
  };
}
